<template>
  <div class="w-full" v-loading="loading">
    <div class="p-2 text-right">
      <el-button v-if="!viewerOnly && !isUnberth" type="primary" size="mini" :disabled="signInfo.signed === 1 || !signInfo.canSign"
        @click="doSign">Sign</el-button>
      <el-button v-if="!viewerOnly && !isUnberth" type="primary" size="mini" :loading="saving" :disabled="signInfo.signed === 1"
        @click="doSave">Save</el-button>
      <el-button plain type="primary" size="mini" @click="backToList">Close</el-button>
    </div>
    <el-alert v-if="isUnberth" class="mx-2" style="margin-bottom: 10px;" type="warning" show-icon
      :closable="false" title="This nomination is marked as Unberth Without Operation." />
    <div class="lc-container">
      <div class="lc-form-header">Loss Control — Ullage Report</div>
      <el-row>
        <el-col :span="24" :md="6">
          <div class="left">
            <div v-if="createdtable" class="lc-nom-info">
              <div class="title">Nomination Information</div>
              <div class="row" v-for="f in infoFields" :key="f.label">
                <div class="k">{{ f.label }} :</div>
                <div class="v">{{ f.value }}</div>
              </div>

              <div class="lc-line"></div>

              <div class="sig-title">Signature</div>
              <div class="sig-rep">Loss Control Representative</div>
              <div class="sig-row"><span>Name:</span><span class="ml-2">{{ signInfo.signedBy || '—' }}</span></div>
              <div class="sig-row"><span>Date and Time:</span><span class="ml-2">{{ formatDateTime(signInfo.signTime) }}</span></div>
              <div class="sig-row"><span>Signature:</span>
                <img v-if="signInfo.signImage" :src="signInfo.signImage" class="ml-2" style="width: 120px;" />
              </div>
            </div>
          </div>
        </el-col>
        <el-col :span="24" :md="18">
          <div class="right">
            <LossControlUllageReport v-if="id" ref="report" :id="id" :readonly="isUnberth" @loaded="signInfo = $event" />
          </div>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script>
import LossControlUllageReport from "./components/LossControlUllageReport.vue";
import { formall } from "@/services/user";
import moment from "moment";

export default {
  name: "LossControl",
  components: {
    LossControlUllageReport,
  },
  data() {
    return {
      id: null,
      createdtable: null,
      loading: true,
      signInfo: {},
      saving: false,
    };
  },
  computed: {
    // Admin (userType 0) and Terminal Logistics (userType 1, role 1) open this read-only from
    // the dashboard — no Sign/Save, and Close returns to the Loss Control Performance tab.
    viewerOnly() {
      const u = this.$store.state.userInfo;
      return String(u.userType) === "0" || (String(u.userType) === "1" && String(u.role) === "1");
    },
    // Unberth Without Operation: no loading happened, so the Loss Control ullage report is view-only
    // (banner shown, no Save/Sign, all fields disabled).
    isUnberth() {
      return !!(this.createdtable && this.createdtable.unberthWithoutOperation == 1);
    },
    infoFields() {
      const d = this.createdtable || {};
      const tanks = (d.tlistNomination || []).map((t) => t.tankNumber).filter(Boolean).join(", ");
      return [
        { label: "Customer", value: d.customerCompanyName },
        { label: "Barge Name (IMO No.)", value: d.vesselName ? `${d.vesselName} (${d.bargeName})` : d.bargeName },
        { label: "Product", value: d.product },
        { label: "Nominated Quantity", value: d.nominatedQtyMax },
        { label: "Tank", value: tanks },
        { label: "Berth Assign Timing", value: d.berthAssignTiming },
        { label: "Jetty Number", value: d.jettyNo },
      ];
    },
  },
  created() {
    this.id = parseInt(this.$route.query.id);
    if (!this.id || this.id <= 0 || !this.isLossControl()) {
      this.backToList();
      return;
    }
    this.getDetail();
  },
  methods: {
    isLossControl() {
      // Who can open the ullage report from the dashboard Loss Control Performance list:
      // Admin (userType 0), plus terminal Loss Control (role 17) and Terminal Logistics (role 1).
      const u = this.$store.state.userInfo;
      if (String(u.userType) === "0") return true;
      return String(u.userType) === "1" && ["17", "1"].includes(String(u.role));
    },
    backToList() {
      if (this.viewerOnly) {
        localStorage.setItem("dashboardActiveTab", "lossControl");
        this.$router.push({ path: "/dashboard/index" });
        return;
      }
      this.$router.push({ path: "/barging/index" });
    },
    doSave() {
      this.saving = true;
      Promise.resolve(this.$refs.report && this.$refs.report.save())
        .finally(() => {
          this.saving = false;
        });
    },
    doSign() {
      if (this.$refs.report) this.$refs.report.handleSign();
    },
    formatDateTime(v) {
      if (!v) return "—";
      const m = moment(v);
      return m.isValid() ? m.format("DD/MM/YYYY HH:mm:ss") : v;
    },
    async getDetail() {
      await formall(this.id).then((res) => {
        this.createdtable = res.data;
        this.loading = false;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.lc-container {
  width: 100% !important;
  background-color: white;
}

.lc-form-header {
  padding: 16px 20px;
  font-weight: 600;
  font-size: 16px;
  color: #4C565C;
  border-bottom: 1px solid #D5DDED;
}

.left {
  border-right: 1px solid #D5DDED;
  min-height: 100%;
}

.lc-nom-info {
  padding: 20px;
  color: #1D1B20;

  .title {
    font-size: 14px;
    font-weight: 800;
    margin-bottom: 12px;
  }

  .row {
    display: flex;
    align-items: baseline;
    margin-bottom: 8px;
    font-size: 12px;
  }

  .k {
    flex: 0 0 110px;
    font-weight: 700;
    color: #4C565C;
    padding-right: 8px;
    word-break: keep-all;
    overflow-wrap: normal;
  }

  .v {
    flex: 1;
    font-weight: 600;
    color: #1D1B20;
    word-break: break-word;
    text-transform: uppercase;
  }

  .lc-line {
    border-top: 1px solid #D5DDED;
    margin: 16px 0;
  }

  .sig-title {
    font-size: 14px;
    font-weight: 800;
    color: #4C565C;
    margin-bottom: 12px;
  }

  .sig-rep {
    font-size: 12px;
    font-weight: 700;
    margin-bottom: 10px;
  }

  .sig-row {
    display: flex;
    align-items: center;
    font-size: 12px;
    font-weight: 600;
    color: #4C565C;
    margin-bottom: 10px;
  }
}
</style>
